import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { 
  Upload, FileText, CheckCircle, XCircle, AlertCircle, Users, 
  RefreshCw, Send, Loader2, Download, ChevronRight, ArrowLeft,
  Mail, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const CSVImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [imports, setImports] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(null);
  
  // Manual import form
  const [manualForm, setManualForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    city: '',
    state: '',
    languages: ''
  });

  useEffect(() => {
    fetchImports();
    fetchPendingUsers();
  }, []);

  const fetchImports = async () => {
    try {
      const res = await api.get('/csv-import/imports');
      setImports(res.data);
    } catch (e) {
      console.error('Failed to fetch imports:', e);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const res = await api.get('/csv-import/pending-onboarding');
      setPendingUsers(res.data);
    } catch (e) {
      console.error('Failed to fetch pending users:', e);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.csv')) {
      setSelectedFile(file);
    } else {
      toast.error('Please select a CSV file');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const res = await api.post('/csv-import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const importId = res.data.import_id;
      toast.success(`Import started — processing ${res.data.total_rows} rows in background`);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Poll for progress until complete
      const pollProgress = async () => {
        for (let attempt = 0; attempt < 120; attempt++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const progress = await api.get(`/csv-import/imports/${importId}`);
            const d = progress.data;
            if (d.status === 'completed' || d.status === 'failed') {
              setImportResult({
                import_id: importId,
                total_rows: d.total_rows,
                successful: d.successful || d.successful_imports || 0,
                failed: d.failed || d.failed_imports || 0,
                errors: d.errors || [],
                status: d.status,
              });
              setShowResultDialog(true);
              fetchImports();
              fetchPendingUsers();
              if ((d.successful || d.successful_imports || 0) > 0) {
                toast.success(`Import complete — ${d.successful || d.successful_imports} users created`);
              }
              return;
            }
            // Show progress toast
            const processed = (d.successful || d.successful_imports || 0) + (d.failed || d.failed_imports || 0);
            if (processed > 0 && attempt % 3 === 0) {
              toast.info(`Processing... ${processed} of ${d.total_rows} rows`);
            }
          } catch {
            break;
          }
        }
      };
      pollProgress();
      
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const handleManualImport = async () => {
    if (!manualForm.email || !manualForm.first_name || !manualForm.last_name) {
      toast.error('Email, first name, and last name are required');
      return;
    }
    
    setLoading(true);
    try {
      const languages = manualForm.languages 
        ? manualForm.languages.split(',').map(l => l.trim()).filter(Boolean)
        : [];
      
      await api.post('/csv-import/single', {
        ...manualForm,
        languages
      });
      
      toast.success('Talent imported and welcome email sent!');
      setShowManualDialog(false);
      setManualForm({ email: '', first_name: '', last_name: '', phone: '', city: '', state: '', languages: '' });
      fetchPendingUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import talent');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (userId) => {
    setResendingEmail(userId);
    try {
      await api.post('/csv-import/resend-welcome-email', { user_id: userId });
      toast.success('Welcome email sent!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setResendingEmail(null);
    }
  };

  const downloadTemplate = () => {
    const template = 'email,first_name,last_name,phone,city,state,zip_code,languages\njohn@example.com,John,Doe,555-123-4567,Miami,FL,33101,"English, Spanish"';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'talent_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Talent CSV Import</h1>
          <p className="text-sm text-gray-500">Bulk import talent from CSV files</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Upload Section */}
        <div className="col-span-2 space-y-6">
          {/* Upload Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-cyan-500" />
                Upload CSV
              </CardTitle>
              <CardDescription>
                Upload a CSV file with talent data. Required columns: email, first_name, last_name
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  selectedFile ? 'border-cyan-400 bg-cyan-50' : 'border-gray-200 hover:border-cyan-300'
                }`}
                onClick={() => fileInputRef.current?.click()}
                style={{ cursor: 'pointer' }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="csv-file-input"
                />
                {selectedFile ? (
                  <div className="space-y-2">
                    <FileText className="w-12 h-12 text-cyan-500 mx-auto" />
                    <p className="font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-sm text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <p className="font-medium text-gray-700">Drag & drop or click to upload</p>
                    <p className="text-sm text-gray-500">CSV files only</p>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between mt-4">
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="w-4 h-4 mr-1" /> Download Template
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowManualDialog(true)}>
                    <UserPlus className="w-4 h-4 mr-1" /> Add Single
                  </Button>
                  <Button 
                    onClick={handleUpload} 
                    disabled={!selectedFile || uploading}
                    className="bg-cyan-500 hover:bg-cyan-600"
                    data-testid="upload-csv-btn"
                  >
                    {uploading ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-1" /> Import</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Import History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Import History</span>
                <Button variant="ghost" size="sm" onClick={fetchImports}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {imports.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No imports yet</p>
              ) : (
                <div className="space-y-3">
                  {imports.map(imp => (
                    <div key={imp.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{imp.filename}</p>
                          <p className="text-xs text-gray-500">{new Date(imp.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> {imp.successful}
                            </Badge>
                            {imp.failed > 0 && (
                              <Badge className="bg-red-100 text-red-700">
                                <XCircle className="w-3 h-3 mr-1" /> {imp.failed}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">of {imp.total_rows} total</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending Onboarding */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" />
                Pending Onboarding
              </CardTitle>
              <CardDescription>
                Users who haven't completed their profile
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-4">All users have completed onboarding</p>
              ) : (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {pendingUsers.map(user => (
                    <div key={user.user_id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResendEmail(user.user_id)}
                          disabled={resendingEmail === user.user_id}
                          data-testid={`resend-email-${user.user_id}`}
                        >
                          {resendingEmail === user.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-500">Profile completion</span>
                          <span className="font-medium">{user.profile_completion_percent}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div 
                            className="bg-cyan-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${user.profile_completion_percent}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="mt-4">
            <CardContent className="pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">{pendingUsers.length}</div>
                <div className="text-sm text-gray-500">Awaiting profile completion</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Import Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="bg-white max-w-md" data-testid="import-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.failed === 0 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-500" />
              )}
              Import Complete
            </DialogTitle>
          <DialogDescription className="sr-only">Import Complete</DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{importResult.total_rows}</div>
                  <div className="text-xs text-gray-500">Total Rows</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{importResult.successful}</div>
                  <div className="text-xs text-gray-500">Imported</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{importResult.failed}</div>
                  <div className="text-xs text-gray-500">Failed</div>
                </div>
              </div>
              
              {importResult.errors?.length > 0 && (
                <div className="bg-red-50 p-3 rounded-lg max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
                  {importResult.errors.slice(0, 10).map((err, i) => (
                    <p key={i} className="text-xs text-red-600">
                      Row {err.row}: {err.error}
                    </p>
                  ))}
                  {importResult.errors.length > 10 && (
                    <p className="text-xs text-red-500 mt-1">
                      ...and {importResult.errors.length - 10} more
                    </p>
                  )}
                </div>
              )}
              
              <p className="text-sm text-gray-600">
                Welcome emails have been sent to all successfully imported users.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowResultDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Import Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent className="bg-white max-w-md" data-testid="manual-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-cyan-500" />
              Add Single Talent
            </DialogTitle>
            <DialogDescription>
              Manually add a talent. They will receive a welcome email to set up their account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First Name *</Label>
                <Input 
                  value={manualForm.first_name}
                  onChange={e => setManualForm({ ...manualForm, first_name: e.target.value })}
                  placeholder="John"
                  data-testid="manual-first-name"
                />
              </div>
              <div>
                <Label className="text-xs">Last Name *</Label>
                <Input 
                  value={manualForm.last_name}
                  onChange={e => setManualForm({ ...manualForm, last_name: e.target.value })}
                  placeholder="Doe"
                  data-testid="manual-last-name"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input 
                type="email"
                value={manualForm.email}
                onChange={e => setManualForm({ ...manualForm, email: e.target.value })}
                placeholder="john@example.com"
                data-testid="manual-email"
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input 
                value={manualForm.phone}
                onChange={e => setManualForm({ ...manualForm, phone: e.target.value })}
                placeholder="555-123-4567"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">City</Label>
                <Input 
                  value={manualForm.city}
                  onChange={e => setManualForm({ ...manualForm, city: e.target.value })}
                  placeholder="Miami"
                />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input 
                  value={manualForm.state}
                  onChange={e => setManualForm({ ...manualForm, state: e.target.value })}
                  placeholder="FL"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Languages (comma-separated)</Label>
              <Input 
                value={manualForm.languages}
                onChange={e => setManualForm({ ...manualForm, languages: e.target.value })}
                placeholder="English, Spanish"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleManualImport} 
              disabled={loading}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="manual-import-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Import & Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CSVImport;
